import { Palette } from 'lucide-react';

/** Backend default (`#003366`). Kept in sync with service.DefaultPDFTheme(). */
export const DEFAULT_DOCUMENT_COLOR = '#003366';

/**
 * Accent presets. All are dark enough to carry white header text, so the
 * common path never produces an unreadable document; the native picker
 * beside them still allows any custom colour.
 */
const PRESETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: DEFAULT_DOCUMENT_COLOR, label: 'Navy (Bawaan)' },
  { value: '#0F766E', label: 'Teal' },
  { value: '#14532D', label: 'Hijau' },
  { value: '#7F1D1D', label: 'Maroon' },
  { value: '#5B21B6', label: 'Ungu' },
  { value: '#334155', label: 'Abu Gelap' },
];

/**
 * Relative luminance (WCAG). Mirrors contrastingText() in the Go service so
 * the preview shows the same black/white header text the PDF will use.
 */
function isLightColor(hex: string): boolean {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return false;

  const channels = [0, 2, 4].map(i => {
    const s = parseInt(m[1].slice(i, i + 2), 16) / 255;
    if (s <= 0.04045) return s / 12.92;
    const x = (s + 0.055) / 1.055;
    return x * x;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2] > 0.5;
}

interface DocumentColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

export default function DocumentColorPicker({ value, onChange, disabled = false }: Readonly<DocumentColorPickerProps>) {
  const headerTextColor = isLightColor(value) ? '#000000' : '#FFFFFF';

  return (
    <div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Palette size={16} className="text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-white">Warna Template Dokumen</span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Warna ini dipakai untuk judul, header tabel, dan bagan struktur pada dokumen yang diunduh.
      </p>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map(preset => {
            const isActive = preset.value.toLowerCase() === value.toLowerCase();
            return (
              <button
                key={preset.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange(preset.value)}
                title={preset.label}
                aria-label={preset.label}
                aria-pressed={isActive}
                className={`h-7 w-7 rounded-full border-2 transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? 'border-gray-900 dark:border-white scale-110'
                    : 'border-transparent hover:scale-105 ring-1 ring-gray-300 dark:ring-gray-600'
                }`}
                style={{ background: preset.value }}
              />
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="document-color-custom" className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
            Warna lain
          </label>
          <input
            id="document-color-custom"
            type="color"
            value={value}
            disabled={disabled}
            onChange={e => onChange(e.target.value)}
            className="h-8 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-xs font-mono uppercase text-gray-500 dark:text-gray-400">{value}</span>
        </div>

        {/* Mirrors how the PDF will render a table header, including the
            automatic switch to dark text on a light accent. */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 dark:text-gray-300">Pratinjau</span>
          <span
            className="rounded px-3 py-1 text-xs font-semibold"
            style={{ background: value, color: headerTextColor }}
          >
            Header Tabel
          </span>
        </div>
      </div>
    </div>
  );
}
